import { useEffect, useRef } from 'react';
import type { KeyContext, KeyEventLike } from '@visious/core';

/**
 * The DOM half of §7's keymap: turning a `KeyboardEvent` into the record core's `keyToCommand` takes,
 * and getting those events in the first place.
 *
 * Both look like exceptions to R2 and are not. Core's keymap is a decision *table* and lives in core;
 * what is left here is classifying a DOM event target and choosing a listener, neither of which core
 * can express and neither of which is a decision about what a key means.
 */

/**
 * What the event landed on. §7 says "skip when `target` is an input", and the three cases are why that
 * sentence is not one check — see core's `KeyTarget`.
 */
export function keyTargetOf(target: EventTarget | null): KeyContext['target'] {
  if (!(target instanceof HTMLElement)) return 'other';
  if (target.isContentEditable) return 'text';

  const tag = target.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return 'text';
  if (tag === 'BUTTON' || tag === 'A') return 'button';
  if (target instanceof HTMLInputElement) {
    if (target.dataset['role'] === 'volume') return 'volume';
    // `range` is the scrub bar; every other input type is something being typed into.
    return target.type === 'range' ? 'other' : 'text';
  }
  return 'other';
}

export function describeKey(event: KeyboardEvent): KeyEventLike {
  return {
    key: event.key,
    // The key's *place*, which is how the frame-step pair is addressed (core's `KeyEventLike`).
    code: event.code,
    ctrl: event.ctrlKey,
    alt: event.altKey,
    meta: event.metaKey,
    shift: event.shiftKey,
    repeat: event.repeat,
  };
}

/** `Space` and its ancient alias — the one key this listener leaves to the page. */
const PAGE_KEYS = new Set([' ', 'Spacebar']);

/**
 * The keymap, reachable while focus is anywhere on the page — "skipping with the keyboard doesn't
 * [work] when the player isn't focussed" was the complaint. §7 put the handler on the player element and said never on
 * `document`, because "the page has other keyboard consumers" — which is true and is what the three
 * declinations below are, rather than a reason to make the shortcuts unreachable:
 *
 * - **`defaultPrevented`** — the player surface's own handler runs first (React listens at the root
 *   element, so anything it handled has already been marked) and so does any other component that
 *   claimed the press. This listener only ever sees what nobody wanted.
 * - **`Space`** — outside the player it is the page's scroll, and taking that away is a bigger loss
 *   than gaining a second play/pause. Inside the player the surface's handler has it, as before.
 * - **a text field** — `keyTargetOf` answers `text` and core declines every key. `↑`/`↓` decline on
 *   their own: they are gated on focus actually being inside the player, so scrolling by arrow key
 *   survives everywhere else.
 *
 * Registered by the watch screen's hook, so it exists exactly while a player does — /embed included,
 * where the page *is* the player and this changes nothing.
 */
export function useDocumentPlayerKeys(
  press: (event: KeyEventLike, target: KeyContext['target']) => boolean,
  /**
   * `false` while the player is the minimized one in the corner. The three declinations above
   * are all about *what* was pressed and none of them covers this: an unclaimed `→` outside a text
   * field is exactly what core answers, so a corner player would seek every time the reader tried to
   * move along a row of feed cards. The player being on the page is the fourth condition, and it is
   * the caller's to know.
   */
  enabled = true,
): void {
  // Through a ref so the listener is registered once: `press` is rebuilt whenever the screen's
  // handlers are, and re-binding a document listener on every one of those is churn with a window in
  // it — a press between the removal and the addition would be dropped.
  const pressRef = useRef(press);
  useEffect(() => {
    pressRef.current = press;
  }, [press]);

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || PAGE_KEYS.has(event.key)) return;
      if (pressRef.current(describeKey(event), keyTargetOf(event.target))) event.preventDefault();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
